import React, { useEffect, useState } from 'react';
import DataTable from '../../../ui/DataTable/DataTable';
import { CellNumber, CellMoney, CellText, CellPlate } from '../../../ui/DataTable/cells';
import { StatGrid } from '../../../ui/Stat/Stat';
import { ICONS } from '../../../ui/icon-map';
import Dialog from '../../../ui/Dialog/Dialog';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { finesService, fmtAED, errorMessage } from '../../../services/finesService';
import { StatCard, Pill, formatPlate } from './ui';
import { useRowLinks } from './jobLinks';
import { checkedUpTo, paidOn } from './rtaPayments';

// Fines RTA no longer lists as owed (paid) that no receipt holds (owner, 15 Oct 2026): not in the company RTA account's
// history the engine reads every week and not uploaded under Payments - a payment nobody can show yet.
function NoReceiptModal({ data, onClose }) {
    const { FineLink, ContractLink, modals } = useRowLinks();
    const rows = data.rows || [];
    return (
        <Dialog open onClose={onClose} size="xl" title="Gone from RTA - no receipt">
            <div className="text-sm text-gray-600 mb-3">
                RTA lists these fines as paid, but no receipt holds them: they are not in the company RTA account&apos;s history (read every
                week) and were not uploaded under Fines → Payments. A fine paid on RTA&apos;s public fines page shows only once its receipt PDF is uploaded.
                {data.covered_to && <b className="ml-1 text-gray-900">{[`${rows.length} fine(s)`, fmtAED(data.amount), checkedUpTo(data.covered_to)].filter(Boolean).join(' · ')}</b>}
            </div>
            {/* before the first read of the company account nothing can be said (16 Oct 2026 review) */}
            {!data.covered_to ? <div className="text-sm text-gray-500">The company RTA account has not been read yet: this list starts after its first weekly read (or Fines → Data &amp; tools → Read RTA account payments now).</div>
                : rows.length === 0 ? <div className="text-sm text-gray-500">None: every fine gone from RTA up to {paidOn(data.covered_to)} has its receipt.</div> : (
                <TableContainer>
                    <Table sticky>
                        <TableHead><TableRow>
                            <TableHeader>#</TableHeader><TableHeader>Fine</TableHeader><TableHeader>Plate</TableHeader><TableHeader>Fine date</TableHeader>
                            <TableHeader>Amount</TableHeader><TableHeader>Source</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Customer</TableHeader>
                            <TableHeader>Paid detected</TableHeader><TableHeader>On Ejar</TableHeader>
                        </TableRow></TableHead>
                        <TableBody>{rows.map((r, i) => (
                            <TableRow key={r.ticket}>
                                <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                <TableCell><FineLink ticket={r.ticket} /></TableCell>
                                <TableCell><CellPlate>{formatPlate(r.plate)}</CellPlate></TableCell>
                                <TableCell><span className="ds-cell-date">{r.fine_date}</span></TableCell>
                                <TableCell><CellMoney value={Number(r.amount || 0)} currency="AED" /></TableCell>
                                <TableCell><CellText>{r.fines_source}</CellText></TableCell>
                                <TableCell>{r.contract_no ? <ContractLink contract={r.contract_no} customer={r.customer} plate={r.plate} /> : '—'}</TableCell>
                                <TableCell><CellText>{r.customer || '—'}</CellText></TableCell>
                                <TableCell><span className="ds-cell-date">{r.paid_detected_date || '—'}</span></TableCell>
                                <TableCell>{r.ejar_status ? <Pill tone="gray" size="sm">{r.ejar_status}</Pill> : '—'}</TableCell>
                            </TableRow>))}</TableBody>
                    </Table>
                </TableContainer>
            )}
            {modals}
        </Dialog>
    );
}

export default function OverviewTab({ onOpenFines }) {
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [noReceipt, setNoReceipt] = useState(null);       // {rows, total, amount, covered_to} | {error}; null while read
    const [showNoReceipt, setShowNoReceipt] = useState(false);

    useEffect(() => { finesService.stats().then(setStats).catch((e) => setError(errorMessage(e))); }, []);
    // its own read: a slow or failed receipts read never holds the overview back
    useEffect(() => { finesService.rtaNoReceipt().then((r) => setNoReceipt(r || { rows: [] })).catch((e) => setNoReceipt({ error: errorMessage(e) })); }, []);

    if (error) return <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>;
    if (!stats) return <LoadingSpinner message="Loading…" />;

    const unpaid = stats.by_status?.Unpaid || { count: 0, amount: 0, billed: 0 };
    const paid = stats.by_status?.Paid || { count: 0, amount: 0, billed: 0 };
    const last = stats.last_run;
    const runDay = last?.started_at ? String(last.started_at).slice(0, 10) : '';
    const noReceiptCount = noReceipt && !noReceipt.error ? (noReceipt.total ?? (noReceipt.rows || []).length) : 0;
    // how far the engine's read of the company RTA account has come (`covered_to`), said under the count so the number
    // is read with its date: a payment after that day is not read yet (owner, 15 Oct 2026)
    const notReadYet = !!noReceipt && !noReceipt.error && !noReceipt.covered_to;     // no account read yet: no count
    const noReceiptSub = !noReceipt ? 'reading the receipts…' : noReceipt.error ? `could not read: ${noReceipt.error}`
        : notReadYet ? 'the company RTA account is not read yet'
            : [fmtAED(noReceipt.amount), checkedUpTo(noReceipt.covered_to)].filter(Boolean).join(' · ');

    const branchColumns = [
        // the engine's bucket name for fines with no contract, said in words (the key stays for the filter)
        { key: 'branch', header: 'Branch', cell: (b) => (b.branch === 'Unassigned_Fines' ? 'Not matched to a contract' : b.branch) },
        { key: 'count', header: 'Fines', type: 'number' },
        {
            key: 'unpaid', header: 'Unpaid',
            cell: (b) => (
                <button type="button" className="hover:underline"
                        onClick={(e) => { e.stopPropagation(); onOpenFines({ branch: b.branch, status: 'Unpaid' }); }}
                        aria-label={`Open unpaid fines of ${b.branch}`}>
                    <CellNumber value={b.unpaid} />
                </button>
            ),
        },
        { key: 'amount', header: 'Amount', accessor: (b) => b.billed ?? b.amount, cell: (b) => <CellMoney value={Number(b.billed ?? b.amount ?? 0)} currency="AED" /> },
    ];

    return (
        <div className="space-y-6">
            <StatGrid min={180}>
                <StatCard label="Total fines" value={stats.total.count} sub={fmtAED(stats.total.billed ?? stats.total.amount)} onClick={() => onOpenFines({})} />
                <StatCard label="Unpaid" value={unpaid.count} sub={fmtAED(unpaid.billed ?? unpaid.amount)} tone="amber" onClick={() => onOpenFines({ status: 'Unpaid' })} />
                <StatCard label="Paid" value={paid.count} sub={`${fmtAED(paid.billed ?? paid.amount)} · company ${stats.paid_by?.company?.count ?? 0} · customer ${stats.paid_by?.customer?.count ?? 0} (never billed)`} tone="green" onClick={() => onOpenFines({ status: 'Paid' })} />
                <StatCard label="Gone from RTA - no receipt"
                          value={!noReceipt ? '…' : noReceipt.error || notReadYet ? '—' : noReceiptCount}
                          sub={noReceiptSub}
                          tone={noReceiptCount ? 'amber' : 'slate'}
                          onClick={noReceipt && !noReceipt.error ? () => setShowNoReceipt(true) : undefined} />
                <StatCard label="Unassigned" value={stats.unassigned.count} sub={fmtAED(stats.unassigned.billed ?? stats.unassigned.amount)} tone="red" onClick={() => onOpenFines({ match: 'unassigned' })} />
                <StatCard label="Vehicle impound" value={stats.impound?.count ?? 0} sub={fmtAED(stats.impound?.billed ?? 0)} tone="red" onClick={() => onOpenFines({ impound: 'with' })} />
                <StatCard label="Needs review" value={stats.needs_review?.count ?? 0} sub={stats.needs_review?.count ? 'plate inactive or overlapping contracts' : 'nothing to check'} tone={stats.needs_review?.count ? 'amber' : 'slate'} onClick={() => onOpenFines({ review: '1' })} />
            </StatGrid>
            <StatGrid min={180}>
                <StatCard label="With image" value={stats.images?.with ?? 0} tone="green" onClick={() => onOpenFines({ has_image: 'with' })} />
                <StatCard label="Without image" value={stats.images?.without ?? 0} tone={stats.images?.without ? 'red' : 'slate'} onClick={() => onOpenFines({ has_image: 'without' })} />
                <StatCard label="From RTA portal" value={stats.images?.portal ?? 0} tone="slate" onClick={() => onOpenFines({ has_image: 'portal' })} />
                <StatCard label="Offline cards" value={stats.images?.offline ?? 0} tone={stats.images?.offline ? 'amber' : 'slate'} onClick={() => onOpenFines({ has_image: 'offline' })} />
                {Object.entries(stats.tabs || {}).map(([name, n]) => (
                    <StatCard key={name} label={name} value={n} tone={/non-payable/i.test(name) ? 'red' : /black/i.test(name) ? 'amber' : 'green'} onClick={() => onOpenFines({ tab: name })} />
                ))}
            </StatGrid>
            {last && (
                <div>
                    <h3 className="ds-section-title">Last run: {last.run_date} <small>({last.started_at})</small></h3>
                    <StatGrid min={180}>
                        <StatCard label="Fines in export" value={last.total_fines ?? '—'} tone="slate" onClick={() => onOpenFines({})} />
                        <StatCard label="New fines" value={last.new_fines ?? '—'} tone="slate" onClick={() => onOpenFines({ first_seen: runDay })} />
                        <StatCard label="Paid detected" value={last.paid_fines ?? '—'} tone="slate" onClick={() => onOpenFines({ status: 'Paid', paid_on: runDay })} />
                        <StatCard label="Unassigned" value={last.unassigned_fines ?? '—'} tone="slate" onClick={() => onOpenFines({ match: 'unassigned' })} />
                        <StatCard label="Images portal / offline" value={`${last.screenshots_portal ?? 0} / ${last.screenshots_offline ?? 0}`} tone="slate" />
                    </StatGrid>
                </div>
            )}
            <DataTable
                title="By branch"
                columns={branchColumns}
                data={stats.by_branch}
                rowKey="branch"
                searchable={false}
                pagination={false}
                columnToggle={false}
                onRowClick={(b) => onOpenFines({ branch: b.branch })}
                emptyIcon={ICONS.fines}
                emptyTitle="No fines yet"
            />
            {showNoReceipt && noReceipt && !noReceipt.error && <NoReceiptModal data={noReceipt} onClose={() => setShowNoReceipt(false)} />}
        </div>
    );
}
